import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Lock, ArrowRight, Lightning, SignOut, CheckSquare, Square } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

export default function CheckoutCancel() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [addOrderBump, setAddOrderBump] = useState(false);

    const startCheckout = async () => {
        setLoading(true);
        try {
            const r = await api.post("/stripe/create-checkout", {
                origin_url: window.location.origin,
                add_visual_authority_guide: addOrderBump,
            });
            window.location.href = r.data.url;
        } catch (err) {
            toast.error(err.response?.data?.detail || "CHECKOUT FAILED");
            setLoading(false);
        }
    };

    const totalPrice = addOrderBump ? 67 : 20;

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-6 grid-bg relative py-12">
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background pointer-events-none" />

            <button
                data-testid="cancel-logout-btn"
                onClick={() => { logout(); navigate("/"); }}
                className="absolute top-6 right-6 flex items-center gap-2 text-muted-foreground hover:text-white text-xs label-mono"
            >
                <SignOut size={16} /> LOGOUT
            </button>

            <div className="relative w-full max-w-2xl">
                <div className="flex items-center gap-2 justify-center mb-8">
                    <div className="w-10 h-10 bg-electric-blue flex items-center justify-center neon-border">
                        <Lightning weight="fill" size={22} className="text-white" />
                    </div>
                    <span className="font-heading font-black tracking-tighter text-2xl">
                        NEXUS<span className="text-electric-blue">.</span>LAB
                    </span>
                </div>

                <div className="glass-panel p-10 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric-blue to-transparent" />
                    <div className="absolute inset-0 grain-overlay pointer-events-none" />

                    <div className="flex items-center gap-3 mb-6">
                        <Lock size={28} weight="fill" className="text-electric-blue" />
                        <p className="label-mono text-electric-blue">VAULT LOCKED</p>
                    </div>

                    <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight mb-4">
                        One step from <br />
                        <span className="text-electric-blue">the finish line.</span>
                    </h1>

                    <p className="text-muted-foreground mb-8 max-w-lg leading-relaxed">
                        Welcome, <span className="text-white font-semibold">{user?.display_name}</span>.
                        Your operator profile is locked in. Complete the Founder checkout to unlock the Recipe Vault, Script Engine, and Leaderboard.
                    </p>

                    <div className="bg-[#0A0A0A] border border-white/10 p-6 mb-6">
                        <div className="flex justify-between items-baseline mb-3">
                            <span className="label-mono">FOUNDER TIER</span>
                            <span className="label-mono text-electric-blue">SEATS 101–500</span>
                        </div>
                        <div className="flex items-baseline gap-2 mb-2">
                            <span className="font-heading text-5xl font-black text-electric-blue">${totalPrice}</span>
                            <span className="text-muted-foreground">/ month</span>
                        </div>
                        <p className="text-xs text-muted-foreground italic">
                            Locked-in rate for the life of your membership.
                        </p>
                    </div>

                    {/* ORDER BUMP */}
                    <div
                        onClick={() => setAddOrderBump(!addOrderBump)}
                        data-testid="order-bump-checkbox"
                        className={`bg-[#0A0A0A] border-2 p-6 mb-8 cursor-pointer transition-all hover:border-yellow-400/50 ${
                            addOrderBump ? "border-yellow-400" : "border-white/20"
                        }`}
                    >
                        <div className="flex items-start gap-4">
                            <div className="mt-1">
                                {addOrderBump ? (
                                    <CheckSquare size={24} weight="fill" className="text-yellow-400" />
                                ) : (
                                    <Square size={24} className="text-white/40" />
                                )}
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="label-mono text-yellow-400 text-[10px]">⚡ ORDER BUMP</span>
                                    <span className="font-heading text-xl font-black text-yellow-300">+$47 ONE-TIME</span>
                                </div>
                                <h3 className="font-heading text-lg font-bold mb-2">
                                    Add The Visual Authority Guide
                                </h3>
                                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                                    Unlock <span className="text-white font-semibold">Strategy Guide #002</span> — the 
                                    'Mad-Lib' headline swipe file + Vegas-Tech thumbnail psychology that Sarah used to hit 
                                    <span className="text-electric-blue"> 50K followers in 90 days</span>. 
                                    Your CTR ceiling just got higher.
                                </p>
                                <div className="flex items-center gap-2">
                                    <div className="w-1 h-1 bg-yellow-400 rounded-full" />
                                    <span className="text-xs text-yellow-400 font-semibold">
                                        83% of Founders add this at checkout
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={startCheckout}
                        disabled={loading}
                        data-testid="cancel-checkout-btn"
                        className="w-full bg-electric-blue hover:bg-electric-blue-dark disabled:opacity-50 text-white font-bold py-4 text-sm tracking-wider uppercase transition-all hover:shadow-[0_0_32px_rgba(0,123,255,0.6)] flex items-center justify-center gap-2"
                    >
                        {loading ? "REDIRECTING TO STRIPE..." : <>
                            <CreditCard size={18} weight="fill" />
                            COMPLETE FOUNDER CHECKOUT
                            <ArrowRight size={18} weight="bold" />
                        </>}
                    </button>

                    <p className="text-center text-xs text-muted-foreground mt-4">
                        Secure checkout powered by Stripe. Cancel anytime.
                    </p>
                </div>
            </div>
        </div>
    );
}
